import router from '@/app/router';
import { useQueryCacheManager } from '@/common/composables/useQueryCacheManager';
import { clearSentryUser, configureSentryUser } from '@/common/utils/sentry.utils';
import { STRIPE_PRE_ORDER_PAYMENT_GATEWAY_ID_STORAGE_KEY } from '@/modules/buyCredits/composables/usePaymentPreOrder';
import { useEnterpriseStore } from '@/modules/enterprise/stores/enterprise.store';
import { useUserStore } from '@/modules/user/stores/user.store';
import { useStorage } from '@vueuse/core';
import { defineStore, getActivePinia, type Store } from 'pinia';
import { computed } from 'vue';
import {
  loginNewClientService,
  loginService,
  logoutService,
  registerService,
} from '../services/auth.service';
import type { Auth } from '../types/auth.interface';
import type { LoginForm } from '../types/loginForm.interface';
import type { RegisterForm } from '../types/registerForm.interface';

/**
 * @description: Utility function to reset all active stores
 * @author: Omar Sánchez Martinez
 * @since: 18/06/2025
 */
const resetAllActiveStores = (): void => {
  const pinia = getActivePinia();
  if (pinia) {
    try {
      // Use type assertion to access internal store registry
      const storeRegistry = (pinia as unknown as { _s: Map<string, Store> })._s;
      storeRegistry.forEach((store: Store, storeId: string) => {
        try {
          // Use the plugin's $reset method which intelligently handles custom reset methods
          if ('$reset' in store && typeof store.$reset === 'function') {
            store.$reset();
          }
        } catch (storeError) {
          console.warn(`Error resetting store "${storeId}":`, storeError);
          // Continue with other stores even if one fails
        }
      });
    } catch (error) {
      console.warn('Error accessing store registry:', error);
    }
  }
};

export const useAuthStore = defineStore('auth', () => {
  const token = useStorage<string | null>('accessToken', null);
  const expirationDate = useStorage<number | null>('expirationDate', null);

  // Initialize query cache manager
  const { clearAllCache } = useQueryCacheManager();

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Limpia todos los datos del usuario y la empresa activas
   */
  const destroyLoginInfo = async () => {
    // Clear TanStack Query cache to prevent showing data from previous session
    clearAllCache();

    // Reset all active stores using utility function
    resetAllActiveStores();

    // Clear additional auth-specific data
    clearSentryUser();
    token.value = null;
    expirationDate.value = null;
    localStorage.removeItem(STRIPE_PRE_ORDER_PAYMENT_GATEWAY_ID_STORAGE_KEY);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Destruye la información de la sesión del usuario
   */
  const logout = async () => {
    const success = await logoutService();
    if (success) {
      await destroyLoginInfo();
      await router.replace({ name: 'login' });
    } else {
      throw new Error('Error al cerrar sesión, por favor intenta nuevamente');
    }
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Inicia sesión en la aplicación
   */
  const login = async (data: LoginForm): Promise<Auth> => {
    await destroyLoginInfo();
    const { email, password } = data;

    const loginResponse = await loginService(email, password);

    if (!loginResponse.new_client_token) {
      await _setLoginInfo(loginResponse);
    }

    return loginResponse;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 03/06/2025
   * @description: Registra un nuevo usuario y retorna el token de registro
   */
  const register = async (data: RegisterForm): Promise<string> => {
    const newClientToken = await registerService(data);
    return newClientToken;
  };

  /**
   * @author: Cascade
   * @since: 31/10/2025
   * @description: Inicializa la sesión del usuario con la respuesta de autenticación proporcionada
   */
  const initializeSessionFromAuth = async (authResponse: Auth): Promise<void> => {
    await destroyLoginInfo();
    await _setLoginInfo(authResponse);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 08/06/2025
   * @description: Inicia sesión para un nuevo cliente usando su token
   */
  const loginNewClient = async (newClientToken: string): Promise<void> => {
    const loginResponse = await loginNewClientService(newClientToken);

    await _setLoginInfo(loginResponse);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 19/06/2025
   * @description: Establece la información de inicio de sesión en el store y configura Sentry.
   */
  const _setLoginInfo = async (loginResponse: Auth) => {
    // Clear TanStack Query cache before setting new login info
    // This ensures no data from previous sessions is displayed
    clearAllCache();

    //set user
    useUserStore().setUser(loginResponse.user);

    //set enterprise
    const firstEnterprise = loginResponse.user.enterprises[0];
    if (firstEnterprise) {
      useEnterpriseStore().setActiveEnterprise(structuredClone(firstEnterprise));
    }

    //set expiration date
    const now = new Date();
    expirationDate.value = now.getTime() + loginResponse.expires_in * 1000;

    //set token
    token.value = loginResponse.token;

    // Configure Sentry user tracking
    configureSentryUser(loginResponse.user);
  };

  return {
    // Properties
    token,
    expirationDate,

    // Getters
    isAuthenticated: computed(() => !!token.value),

    // Actions
    destroyLoginInfo,
    login,
    loginNewClient,
    initializeSessionFromAuth,
    logout,
    register,
  };
});
